import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { countBy, keyBy } from 'lodash-es';
import { Spinner, TableList, useOverlayContext, useStateInRef } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useFetchAgreementStudioData } from '~/components/AgreementStudioPage/hooks/useFetchAgreementStudioData';
import GenericError from '~/components/GenericError/GenericError';
import { routes } from '~/constants';
import { useIoContext, useStoreActions } from '~/contexts';
import { useContacts } from '~/hooks/reactQuery/queries/useContacts';
import { useDuplicateRelationship } from '~/hooks/useDuplicateRelationship';
import { useInfiniteScroll } from '~/hooks/useInfiniteScroll';
import { useOnMount } from '~/hooks/useOnMount';
import { useRestfulWrapper } from '~/hooks/useRestfulWrapper';
import { useScheduledProposalActions } from '~/hooks/useScheduledProposal';
import { useTerminateAgreement } from '~/hooks/useTerminateAgreement';
import { useLocaleKeys } from '~/i18n';
import { selectors } from '~/store/state-selectors';
import { useStore, useStoreStatic } from '~/store/store-context';
import DraftUpdateModal from './DraftUpdateModal';
import { editDraftsTableHeaderColumnDefs, selectVisibleDrafts, sortDrafts } from './EditDraftsTable.utils';
import EditDraftsTableRow from './EditDraftsTableRow';
import { useUpdateDraft } from './hooks/useUpdateDraft';
import { ColumnSortId } from './types';
const EDIT_DRAFTS_TABLE_PAGE_SIZE = 30;
export const GetEditDraftsTableTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            EditDraftsTableTID: {
                Root: 'edit-drafts-table',
                Header: 'edit-drafts-table-header',
                Spinner: 'edit-drafts-table-spinner',
                Error: 'edit-drafts-table-error',
                ScrollContainer: 'edit-drafts-table-scroll'
            }
        }
    });
export function EditDraftsTableWithData(props) {
    const { visibleColumns, search, selected, setSelected, aiFilteredIds, changedDraftIds, onHighlightEnd } = props;
    const { EditDraftsTableTID } = useMemo(()=>GetEditDraftsTableTID(), []);
    const { getState } = useStoreStatic();
    const { getNow } = useIoContext();
    const { t: editTableTexts } = useLocaleKeys('pages.draftsDashboard.editTable');
    const columnsTexts = editTableTexts.columns;
    const [drafts, setDrafts] = useState(props.drafts);
    useEffect(()=>setDrafts(props.drafts), [
        props.drafts
    ]);
    const [scrollContainerEl, setScrollContainerEl] = useState(null);
    const [sort, setSort] = useState({
        sortId: ColumnSortId.LastModified,
        sort: 'desc'
    });
    const { contacts } = useContacts();
    const headerItems = useMemo(()=>editDraftsTableHeaderColumnDefs(visibleColumns, columnsTexts), [
        visibleColumns,
        columnsTexts
    ]);
    const contactMap = useMemo(()=>keyBy(contacts || [], (c)=>c.id()), [
        contacts
    ]);
    const getContact = useCallback((draft)=>{
        const clientContactId = draft.clientContactId();
        return clientContactId ? contactMap[clientContactId] || null : null;
    }, [
        contactMap
    ]);
    const anchorSampleLabel = editTableTexts.anchorSample();
    const unnamedDraftLabel = editTableTexts.unnamedDraft();
    const sortedAndFilteredDrafts = useMemo(()=>{
        var _getState_userInfo_businessSettings, _getState_userInfo;
        const filteredDrafts = selectVisibleDrafts({
            drafts,
            getContact,
            search,
            anchorSampleLabel,
            aiFilteredIds
        });
        const sortedDrafts = sortDrafts({
            drafts: filteredDrafts,
            sortId: sort.sortId,
            sort: sort.sort,
            introductoryMessages: (_getState_userInfo = getState().userInfo) === null || _getState_userInfo === void 0 ? void 0 : (_getState_userInfo_businessSettings = _getState_userInfo.businessSettings()) === null || _getState_userInfo_businessSettings === void 0 ? void 0 : _getState_userInfo_businessSettings.introductoryMessages,
            getContact,
            anchorSampleLabel,
            unnamedDraftLabel
        });
        return sortedDrafts;
    }, [
        drafts,
        search,
        sort,
        getContact,
        anchorSampleLabel,
        unnamedDraftLabel,
        aiFilteredIds
    ]);
    const resetKey = useMemo(()=>({
            sort,
            search,
            aiFilteredIds
        }), [
        sort,
        search,
        aiFilteredIds
    ]);
    const { paginatedData, onScroll } = useInfiniteScroll({
        data: sortedAndFilteredDrafts,
        pageSize: EDIT_DRAFTS_TABLE_PAGE_SIZE,
        scrollElRef: scrollContainerEl,
        resetKey
    });
    const updateDraft = useCallback((updatedDraft)=>{
        const withLastModifiedOn = updatedDraft.setLastModifiedOn(getNow().getTime());
        setDrafts((drafts)=>drafts.map((d)=>d.id() === updatedDraft.id() ? withLastModifiedOn : d));
    }, []);
    const deleteDraft = useCallback((draft)=>{
        setDrafts((drafts)=>drafts.filter((d)=>d.id() !== draft.id()));
    }, []);
    const navigate = useNavigate();
    const worksAt = useStore(selectors.user.business);
    const { duplicateDraftFromId } = useDuplicateRelationship();
    const { terminateDraftById } = useTerminateAgreement();
    const { showModal, startLoader, stopLoader } = useOverlayContext();
    const { updateDraft: saveDraft } = useUpdateDraft();
    const { showSwitchToEditModal } = useScheduledProposalActions();
    const getActionDeps = useStateInRef({
        navigate,
        duplicateDraftFromId,
        terminateDraftById,
        showModal,
        startLoader,
        stopLoader,
        saveDraft,
        showSwitchToEditModal,
        updateDraft,
        deleteDraft
    });
    const rowActions = useMemo(()=>({
            editDraft: (draft)=>{
                const deps = getActionDeps();
                if (draft.isScheduled()) {
                    deps.showSwitchToEditModal({
                        draftId: draft.id(),
                        isSampleRelationship: draft.isSample(),
                        refresh: ()=>{}
                    });
                } else {
                    deps.navigate(routes.draft(draft.id()));
                }
            },
            duplicateDraft: (draftId)=>getActionDeps().duplicateDraftFromId(draftId),
            discardDraft: (draft)=>{
                const deps = getActionDeps();
                deps.terminateDraftById(draft.id(), ()=>deps.deleteDraft(draft));
            },
            openDraftUpdateModal: (draft, options)=>{
                const deps = getActionDeps();
                deps.showModal({
                    biName: 'draft_update_modal',
                    closeOnClickOutside: true,
                    closeOnEscapeKey: true,
                    content: (param)=>{
                        let { dismissModal } = param;
                        return /*#__PURE__*/ _jsx(DraftUpdateModal, {
                            skin: options.skin,
                            draft: draft,
                            width: options.width,
                            maxHeight: options.maxHeight,
                            bodyClassName: options.bodyClassName,
                            renderContent: options.renderContent,
                            dismissModal: dismissModal,
                            changeDraftInRow: deps.updateDraft,
                            shouldSubmit: options.shouldSubmit,
                            submitOnNotDirty: options.submitOnNotDirty
                        });
                    }
                });
            },
            addServices: (draft, services, packageId)=>{
                const deps = getActionDeps();
                const newDraft = draft.addServices(services, packageId);
                const loaderName = 'addServicesLoader';
                deps.saveDraft({
                    newDraft,
                    onSuccess: ()=>deps.updateDraft(newDraft),
                    onStartLoader: ()=>deps.startLoader(loaderName),
                    onStopLoader: ()=>deps.stopLoader(loaderName)
                });
            }
        }), []);
    const onToggleAllSelection = useCallback((selectAll)=>setSelected(drafts.reduce((acc, d)=>Object.assign(acc, {
                [d.id()]: selectAll
            }), {})), [
        drafts
    ]);
    const numSelected = useMemo(()=>{
        var _countBy_true;
        return (_countBy_true = countBy(selected).true) !== null && _countBy_true !== void 0 ? _countBy_true : 0;
    }, [
        selected
    ]);
    const tableListSort = useMemo(()=>({
            sort: sort.sort,
            colIndex: headerItems.findIndex((item)=>item.sortId === sort.sortId)
        }), [
        sort,
        headerItems
    ]);
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": EditDraftsTableTID.Root(),
        children: /*#__PURE__*/ _jsx(TableList.Provider, {
            hasActionColumn: true,
            columnDefs: headerItems,
            expandableRows: true,
            expandableRowsVariant: "clickableIcon",
            enableRowSelection: true,
            selectedRows: selected,
            onSelectionChange: setSelected,
            numRows: drafts.length,
            numSelectedRows: numSelected,
            onToggleAllSelection: onToggleAllSelection,
            sort: tableListSort,
            onSortChange: (tableListSort)=>{
                if (!tableListSort) {
                    setSort({
                        sortId: ColumnSortId.PrimaryContact,
                        sort: 'asc'
                    });
                } else {
                    setSort({
                        sortId: headerItems[tableListSort.colIndex].sortId,
                        sort: tableListSort.sort
                    });
                }
            },
            children: /*#__PURE__*/ _jsx(TableList.Structure, {
                containerRef: setScrollContainerEl,
                testId: EditDraftsTableTID.ScrollContainer(),
                onScroll: onScroll,
                header: /*#__PURE__*/ _jsx(TableList.Header, {
                    stickyToTop: true,
                    items: headerItems,
                    testId: EditDraftsTableTID.Header()
                }),
                content: /*#__PURE__*/ _jsx(_Fragment, {
                    children: paginatedData.map((draft, index)=>/*#__PURE__*/ _jsx(EditDraftsTableRow, {
                            draft: draft,
                            index: index,
                            visibleColumns: visibleColumns,
                            getContact: getContact,
                            actions: rowActions,
                            worksAt: worksAt,
                            highlighted: changedDraftIds.has(draft.id()),
                            onHighlightEnd: onHighlightEnd
                        }, draft.id()))
                })
            })
        })
    });
}
const EditDraftsTable = /*#__PURE__*/ forwardRef((param, ref)=>{
    let { visibleColumns, search, selected, setSelected, aiFilteredIds, changedDraftIds, onHighlightEnd } = param;
    const { api } = useStoreActions();
    const { EditDraftsTableTID } = useMemo(()=>GetEditDraftsTableTID(), []);
    const fetchDrafts = ()=>api.draft.getFullDrafts({
            limit: 1000,
            page: 1
        });
    const { refresh, renderRestfulState } = useRestfulWrapper({
        fetch: fetchDrafts
    });
    const fetchAgreementStudioData = useFetchAgreementStudioData({
        disableLoader: true,
        disableAgreements: true,
        disableProposals: true
    });
    useOnMount(()=>{
        fetchAgreementStudioData();
    });
    useImperativeHandle(ref, ()=>({
            refresh
        }));
    return renderRestfulState({
        renderLoader: ()=>/*#__PURE__*/ _jsx(Spinner, {
                centeredInBox: true,
                centerBoxHeight: "100%",
                testId: EditDraftsTableTID.Spinner()
            }),
        renderError: ()=>/*#__PURE__*/ _jsx(GenericError, {
                testId: EditDraftsTableTID.Error()
            }),
        renderChild: (param)=>{
            let { data } = param;
            return /*#__PURE__*/ _jsx(EditDraftsTableWithData, {
                drafts: data,
                visibleColumns: visibleColumns,
                search: search,
                selected: selected,
                setSelected: setSelected,
                aiFilteredIds: aiFilteredIds,
                changedDraftIds: changedDraftIds,
                onHighlightEnd: onHighlightEnd
            });
        }
    });
});
export default EditDraftsTable;
